import {
  OrderSummary,
  Typography,
  useWidgetReadyAfterInitialLoad,
  WidgetReadyBoundary,
} from '@components'
import { useActiveCustomerOrders, useAddOrderToCart } from '@haus-storefront-react/hooks'
import type { Order } from '@haus-storefront-react/shared-types'
import type { OrderSummaryConfigStyles } from '@components/order-summary'
import { SortOrder } from '@haus-storefront-react/shared-types'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Toast, type ToastItem } from '../../components/toast/toast'
import './OrderHistorySkeleton.css'
import styles from './OrderHistory.module.scss'

const TAKE = 10
const SUCCESS_TOAST_DURATION_MS = 2500
const ERROR_TOAST_DURATION_MS = 5000
const PARTIAL_ERROR_TOAST_DURATION_MS = 7000

export type OrderHistoryConfigStyles = OrderSummaryConfigStyles

interface OrderHistoryWidgetProps {
  configStyles?: OrderHistoryConfigStyles
}

/**
 * Order History widget for Elementor.
 * Passes orders to OrderSummary and renders CTA (Order again).
 */
const OrderHistoryWidget = ({ configStyles }: OrderHistoryWidgetProps) => {
  const { t } = useTranslation()
  const orderListOptions = {
    skip: 0,
    sort: {
      createdAt: SortOrder.Desc,
    },
    take: TAKE,
    filter: {
      state: {
        notEq: 'AddingItems',
      },
    },
  }

  const { data: ordersData, isLoading, error } = useActiveCustomerOrders(orderListOptions)
  const { addOrderToCart, isLoading: isAddingToCart } = useAddOrderToCart()
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [addingOrderCode, setAddingOrderCode] = useState<string | null>(null)
  const toastIdRef = useRef(0)

  const addToast = useCallback((item: Omit<ToastItem, 'id'>) => {
    toastIdRef.current += 1
    setToasts((prev) => [...prev, { ...item, id: toastIdRef.current }])
  }, [])

  const handleDismissToast = useCallback((toast: ToastItem) => {
    setToasts((prev) => prev.filter((item) => item.id !== toast.id))
  }, [])

  const handleAddOrderToCart = useCallback(
    async (order: Order) => {
      setAddingOrderCode(order.code)
      try {
        const result = await addOrderToCart(order)

        if (result.failedProductNames.length > 0) {
          addToast({
            type: 'error',
            description: t('orderHistory.addOrderPartialError'),
            duration: PARTIAL_ERROR_TOAST_DURATION_MS,
          })
        } else {
          addToast({
            type: 'success',
            description: t('orderHistory.addOrderSuccess'),
            duration: SUCCESS_TOAST_DURATION_MS,
          })
        }
      } catch {
        addToast({
          type: 'error',
          description: t('orderHistory.addOrderError'),
          duration: ERROR_TOAST_DURATION_MS,
        })
      } finally {
        setAddingOrderCode(null)
      }
    },
    [addOrderToCart, addToast, t],
  )

  const orders = ordersData?.items ?? []
  const hasSettledContent =
    orders.length > 0 || Boolean(error) || (!isLoading && orders.length === 0)
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent,
    hasVisibleContent: true,
    isLoading,
  })

  if (isLoading) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <div className={styles.orderList} role="status" aria-live="polite" aria-busy="true">
          {t('orderHistory.loading')}
        </div>
      </WidgetReadyBoundary>
    )
  }

  if (error) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <div className={styles.orderList}>
          <div className={styles.error} role="alert">
            <Typography variant="body">{t('orderHistory.error')}</Typography>
          </div>
        </div>
      </WidgetReadyBoundary>
    )
  }

  if (orders.length === 0) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <div className={styles.orderList} role="status" aria-live="polite">
          {t('orderHistory.empty')}
        </div>
      </WidgetReadyBoundary>
    )
  }

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <div className={styles.orderList}>
        <div className={styles.orderListContent} aria-label={t('orderHistory.ordersTitle')}>
          {orders.map((order) => (
            <OrderSummary
              key={order.code}
              order={order}
              renderCta
              onOrderAgain={handleAddOrderToCart}
              isAddingToCart={isAddingToCart && addingOrderCode === order.code}
              configStyles={configStyles}
            />
          ))}
        </div>
        <Toast toasts={toasts} onDismiss={handleDismissToast} />
      </div>
    </WidgetReadyBoundary>
  )
}

export default OrderHistoryWidget
